
import {map,  tap, catchError, retryWhen } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Configuration } from '../models/configuration';
import { Observable ,  of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {api, handleError, retryStrategy} from '../../shared/api-loaders';
import {CustomerConfigurationParameters} from '../models/customerConfigurationParameters';
import { ConfigurationFeatures } from '../models/configuration-features';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ConfigurationService {
  addDefaultParameters(
    configurationId: number,
    addSnf: boolean,
    addSl: boolean,
    addCa: boolean,
    addCaOnScreenKeyBoard: boolean,
    addCaPin: boolean,
    addMt: boolean,
    addIntm: boolean,
    addCc: boolean,
    addCt: boolean,
    addPd: boolean,
    addHh: boolean,
    addBp: boolean,
    addLpc: boolean,
    addCh: boolean,
    addPaymentPortal: boolean,
    addMatrixCareEngage: boolean,
    addContactFamilyChat: boolean,
    addMatrixCareEForms: boolean,
    addSolutionsForBusiness: boolean,
    addSbEfa: boolean,
    addSbPnsm: boolean,
    addRetailTrackerOnline: boolean,
    subdomainName: string,
  ) {
    var data = [];
    if (addSnf) {
      data.push('snf.app.url.root',
        'snf.api.url.root',
        'snf.corpOid',)
    }
    if (addLpc) {
      data.push('lpc.api.url.root',
        'lpc.app.url.root');
    }
    if (addSl) {
      data.push('sl.app.url.root',
        'sl.api.url.root',)
    }
    if (addCa) {
      data.push('ca.app.url.root',
        'ca.feature.sso.callback',
        'ca.connections.snf.enabled',
        'ca.connections.mcsl.enabled',
        'ca.oidcClients.client.clientId',
        'ca.oidcClients.client.discoveryUrl',
        'ca.oidcClients.client.ssoRealm',
        'ca.oidcClients.client.oidcName',
        'ca.oidcClients.client.secretId.encrypted',
        'ca.oidcClients.client.ssoAuthServer',
        'ca.oidcClients.client.ssoCustomerDomain',
        'ca.oidcClients.client.ssoCustomerClaimValuationValue',
        'ca.oidcClients.client.ssoUrlContext',)
    }
    if (addCaOnScreenKeyBoard) {
      data.push('ca.feature.onscreenKeyboard.optionOnLoginPage',)
    }
    if (addCaPin) {
      data.push('ca.feature.pin.enabled',
        'ca.feature.pin.minimumLength',
        'ca.feature.pin.sessionLifetime',
        'ca.feature.pin.maxAttemptsBeforeReset',
        'ca.feature.pin.clearModeIfXFailuresOnDeviceinYMinutes',
        'ca.feature.pin.reuseLifetime',)
    }
    if (addMt) {
      data.push('mt.app.url.root',
        'mt.api.url.root',)
    }
    if (addIntm) {
      data.push('intm.app.url.root',
        'intm.api.url.root',)
    }
    if (addCc) {
      data.push('cc.app.url.root',
        'cc.launcher.enabled',)
    }
    if (addCt) {
      data.push('ct.app.url.root',
        'ct.launcher.enabled',)
    }
    if (addPd) {
      data.push('pd.api.url.users',
        'pd.api.url.reference',)
    }
    if (addHh) {
      data.push('hh.api.url.root',
        'hh.api.url.reference',)
    }
    if(addBp){
      data.push('bp.walkaway.timeout.minutes',
      'bp.sl.elite.enabled',
        "bp.globalsearch.enabled",
      'bp.user.provisioning.config.isdefault');
    }
    if (addCh) {
      data.push('ch.api.url.root',
        'ch.app.url.root',)
    }
    if (addPaymentPortal) {
      data.push('paymentportal.enabled')
    }
    if (addRetailTrackerOnline) {
      data.push('rt.online.enabled')
    }
    if (addMatrixCareEngage) {
      data.push("multicare.mobile.enabled");
    }
    if (addContactFamilyChat) {
      data.push("multicare.mobile.familychat.enabled");
    }
    if (addMatrixCareEForms) {
      data.push("multicare.eforms.enabled");
    }
    if (addSolutionsForBusiness) {
      data.push('sb.module.enabled')
    }
    if (addSbEfa) {
      data.push('sb.module.efa.enabled')
    }
    if (addSbPnsm) {
      data.push('sb.module.pnsm.enabled')
    }

    return this._http
      .post(api.configurationDefaultParameters(configurationId,subdomainName),data).pipe(retryWhen(retryStrategy()));
  }


  constructor(private http: HttpClient, private _http: HttpClient) { }

  getConfigurations(): Observable<Configuration[]> {
    return this._http
      .get(api.configurations()).pipe(
      catchError(handleError),);
  }

  getConfigurationsByStack(): Observable<Configuration[]> {
    return this._http
      .get(api.configurationsByStack()).pipe(
        catchError(handleError),);
  }

  getConfigurationsByCustId(custid: number): Observable<Configuration[]> {
    return this._http
      .get(api.configurationsByCustId(custid)).pipe(
        catchError(handleError),);
  }

  getConfigurationFeaturesByConfigId(configId: string): Observable<ConfigurationFeatures> {
    return this._http
      .get(api.configurationFeaturesByConfigId(configId)).pipe(
        catchError(handleError),);
  }

  isPhysicianFeatureEnabled(configId: string): Observable<boolean> {
    return this.getConfigurationFeaturesByConfigId(configId).pipe(
      map(value => value.sharedComponents.includes('bp.idm.auth.physician.enabled'))
    );
  }

  /** GET */
  getConfiguration(id: number): Observable<Configuration> {
    return this._http
      .get(api.configuration(id)).pipe(
      catchError(handleError),);
  }

  /** GET */
  getConfigurationsZoneLimit(newLimit: number): Observable<string> {
    return this._http
      .get(api.getZoneLimits(newLimit)).pipe(
        catchError(handleError),);
  }

  getCustomerConfigurationParameters(configurationId: number): Observable<CustomerConfigurationParameters> {
    return this._http
      .get(api.customerConfigurationParameters(configurationId)).pipe(
        catchError(handleError),);
  }

  /** PUT */
  updateConfiguration(id: number, configuration: Configuration): Observable<Configuration> {
    return this._http
      .put(api.configuration(id), configuration).pipe(
      catchError(handleError));
  }

  /** POST */
  addConfiguration(configuration: Configuration): Observable<Configuration> {
    return this._http
      .post(api.configurations(), configuration).pipe(
      catchError(handleError),);
  }

  /** DELETE */
  deleteConfiguration(id: number): Observable<Configuration> {
    return this._http
      .delete(api.configuration(id)).pipe(
      catchError(handleError));
  }

  configurationGuidSync(configId: string): Observable<string> {
    return this._http
      .post(api.configurationGuidSync(configId), null, { responseType: 'text' as 'json' }).pipe(
        catchError(handleError));
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a message with the MessageService */
  private log(message: string) {
    console.log(`configuration.service: ${message}`);
  }
}
